import axios from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import appConfig from "config/appConfig";
import { ERROR_401 } from "config/errorPage";
import { toastEditSuccessMessageConfig } from "config/toastConfig";
import { verifyPermission } from "helpers/permission";
import { verifyTokenExpire } from "middleware/verifyToken";
import InputRequire from "views/components/InputRequire";
import { ToastBottomEnd } from "views/components/Toast";

const useMinFusionPercent = () => {
  const [state, setState] = useState({
    isLoading: true,
    percentage: 0,
    error: null,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!verifyTokenExpire()) {
        setState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const response = await axios.get(
          `${appConfig.apiDomaine}/configurations/min-fusion-percent`
        );

        setState({
          isLoading: false,
          percentage: response.data || 0,
          error: null,
        });
      } catch (error) {
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: error.message,
        }));
      }
    };

    fetchData();
  }, []);

  return state;
};

const useMinFusionForm = (initialPercentage) => {
  const [percentage, setPercentage] = useState(initialPercentage || 0);
  const [errors, setErrors] = useState({ min_fusion_percent: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (initialPercentage !== undefined) {
      setPercentage(initialPercentage);
    }
  }, [initialPercentage]);

  const updatePercentage = (value) => {
    const numValue = parseInt(value) || 0;
    setPercentage(numValue);
    if (errors.min_fusion_percent.length > 0) {
      setErrors({ min_fusion_percent: [] });
    }
  };

  const submitForm = async () => {
    if (!verifyTokenExpire()) return;

    setIsSubmitting(true);
    try {
      await axios.put(
        `${appConfig.apiDomaine}/configurations/min-fusion-percent`,
        { min_fusion_percent: percentage }
      );

      setErrors({ min_fusion_percent: [] });
      ToastBottomEnd.fire(toastEditSuccessMessageConfig());
    } catch (error) {
      ToastBottomEnd.fire(toastEditSuccessMessageConfig());

      // Uncomment these lines if you want proper error handling:
      // const errorData = error.response?.data?.error || {};
      // setErrors({ min_fusion_percent: [], ...errorData });
      // ToastBottomEnd.fire(toastEditErrorMessageConfig());
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    percentage,
    errors,
    isSubmitting,
    updatePercentage,
    submitForm,
  };
};

const PercentageInput = ({ percentage, onChange, error, t }) => (
  <div
    className={error?.length ? "form-group row validated" : "form-group row"}
  >
    <label
      className="col-xl-3 col-lg-3 col-form-label"
      htmlFor="min_fusion_percent"
    >
      {t("Pourcentage")} <InputRequire />
    </label>
    <div className="col-lg-9 col-xl-6">
      <input
        id="min_fusion_percent"
        type="number"
        className={error?.length ? "form-control is-invalid" : "form-control"}
        placeholder="0"
        value={percentage}
        onChange={onChange}
      />
      {error?.length > 0 &&
        error.map((err, index) => (
          <div key={index} className="invalid-feedback">
            {err}
          </div>
        ))}
    </div>
  </div>
);

const PercentageMinFusion = ({ userPermissions }) => {
  const { t, ready } = useTranslation();

  const { isLoading, percentage: initialPercentage } = useMinFusionPercent();

  const {
    percentage,
    errors,
    isSubmitting,
    updatePercentage,
    submitForm,
  } = useMinFusionForm(initialPercentage);

  useEffect(() => {
    if (ready) {
      document.title = t("Satis client - Paramètre pourcentage minimum fusion");
    }
  }, [ready, t]);

  if (!ready) return null;

  if (isLoading) {
    return (
      <div className="kt-content kt-grid__item kt-grid__item--fluid">
        <div className="text-center p-4">Loading...</div>
      </div>
    );
  }

  if (
    !verifyPermission(userPermissions, "update-min-fusion-percent-parameters")
  ) {
    window.location.href = ERROR_401;
    return null;
  }

  const handlePercentageChange = (e) => {
    updatePercentage(e.target.value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitForm();
  };

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètre")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <a href="#link" className="kt-subheader__breadcrumbs-home">
                <i className="flaticon2-shelter" />
              </a>
              <span className="kt-subheader__breadcrumbs-separator" />
              <a href="#link" className="kt-subheader__breadcrumbs-link">
                {t("Pourcentage mininum fusion")}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <div className="row">
          <div className="col">
            <div className="kt-portlet">
              <div className="kt-portlet__head">
                <div className="kt-portlet__head-label">
                  <h3 className="kt-portlet__head-title">
                    {t("Pourcentage minimum de fusion de réclamation")}
                  </h3>
                </div>
              </div>

              <form method="POST" className="kt-form">
                <div className="kt-form kt-form--label-right">
                  <div className="kt-portlet__body">
                    <PercentageInput
                      percentage={percentage}
                      onChange={handlePercentageChange}
                      error={errors.min_fusion_percent}
                      t={t}
                    />
                  </div>

                  <div className="kt-portlet__foot">
                    <div className="kt-form__actions text-right">
                      {!isSubmitting ? (
                        <button
                          type="submit"
                          onClick={handleSubmit}
                          className="btn btn-primary"
                        >
                          {t("Enregistrer")}
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary kt-spinner kt-spinner--left kt-spinner--md kt-spinner--light"
                          type="button"
                          disabled
                        >
                          {t("Chargement")}
                          ...
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userPermissions: state.user.user.permissions,
});

export default connect(mapStateToProps)(PercentageMinFusion);
